import { HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CoreActions } from '@next/core-lib';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { UserActions } from '@next/core-lib/user';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, delay, finalize, map, mergeMap, startWith, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import * as AppActions from './actions';
import {
  AppActionTypes,
  ContextChanged,
  EditLegalNitrogenWorkingCoefficient,
  LoadCropAidsRequest,
  LoadCropsRequest,
  LoadCropVarietiesByCropIdRequest,
  LoadSalesOrderPeriodsRequest,
  LoadSoilTypesRequest,
} from './actions';
import { getCrops, getSoilTypes } from './selectors';

import { CropVarietySpecificNormDialogComponent } from '../components/crop-variety-specific-norm/dialog/crop-variety-specific-norm-dialog.component';
import { ImportComponent } from '../components/import/import.component';
import { ImportPhosphateLegalNormComponent } from '../components/import-phosphate-legal-norm/import-phosphate-legal-norm.component';
/* eslint-disable-next-line max-len */
import { LegalNitrogenWorkingCoefficientDialogComponent } from '../components/legal-nitrogen-working-coefficient/dialog/legal-nitrogen-working-coefficient-dialog.component';
import { PhosphateLegalNormDialogComponent } from '../components/phosphate-legal-norm/dialog/phosphate-legal-norm-dialog.component';
/* eslint-disable-next-line max-len */
import { PhosphateLegalWorkingCoefficientDialogComponent } from '../components/phosphate-legal-working-coefficient/dialog/phosphate-legal-working-coefficient-dialog.component';

import { TotalNitrogenLegalNormDialogComponent } from '../components/total-nitrogen-legal-norm/dialog/total-nitrogen-legal-norm-dialog.component';
import {
  Crop,
  SiteState,
  TotalNitrogenLegalNorm,
  LegalNitrogenWorkingCoefficient,
  CropVarietySpecificNorm,
  SoilType,
  PhosphateLegalNorm,
  PhosphateConditionEnum,
  PhosphateLegalWorkingCoefficient,
} from '../shared/models';
import { CropAidService } from '../shared/services/crop-aid.service';
import { CropVarietySpecificNormApiService } from '../shared/services/crop-variety-specific-norm-api.service';
import { CropVarietyApiService } from '../shared/services/crop-variety.service';
import { CropApiService } from '../shared/services/crop.service';
import { DownloadBlobService } from '../shared/services/download-blob.service';
import { FieldReportService } from '../shared/services/field-report.service';
import { ImportService } from '../shared/services/import.service';
import { LegalNitrogenWorkingCoefficientService } from '../shared/services/legal-nitrogen-working-coefficient.service';
import { PhosphateLegalNormApiService } from '../shared/services/phosphate-legal-norm-api.service';
import { PhosphateLegalWorkingCoefficientService } from '../shared/services/phosphate-legal-working-coefficient.service';
import { SalesOrderPeriodService } from '../shared/services/sales-order-period.service';
import { SoilTypeApiService } from '../shared/services/soil-type.service';
import { TotalNitrogenLegalNormApiService } from '../shared/services/total-nitrogen-legal-norm-api.service';

@Injectable()
export class AppEffects {
  protected store = inject<Store<SiteState>>(Store);
  protected actions$ = inject(Actions);
  protected totalNitrogenLegalNormService = inject(TotalNitrogenLegalNormApiService);
  protected legalNitrogenWorkingCoefficientService = inject(LegalNitrogenWorkingCoefficientService);
  protected phosphateLegalWorkingCoefficientService = inject(PhosphateLegalWorkingCoefficientService);
  protected cropVarietySpecificNormService = inject(CropVarietySpecificNormApiService);
  protected phosphateLegalNormService = inject(PhosphateLegalNormApiService);
  private readonly cropService = inject(CropApiService);
  private readonly cropVarietyService = inject(CropVarietyApiService);
  private readonly cropAidService = inject(CropAidService);
  private readonly salesOrderPeriodService = inject(SalesOrderPeriodService);
  private readonly soilTypeService = inject(SoilTypeApiService);
  private readonly dialog = inject(DialogService);
  private readonly locale = inject(LocaleService);
  private readonly importService = inject(ImportService);
  private readonly fieldReportService = inject(FieldReportService);
  private readonly blobService = inject(DownloadBlobService);

  customerInitialized$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.userInitialized),
      switchMap(() => of(new ContextChanged())),
    ),
  );

  customerChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.customerChanged),
      map(() => new ContextChanged()),
    ),
  );

  contextChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.ContextChanged),
      switchMap((_) => {
        const selectableYears = [-1, 0, 1].map((n) => new Date().getFullYear() + n);
        selectableYears.forEach((year) => {
          this.store.dispatch(new LoadSalesOrderPeriodsRequest(year));
        });
        return [new LoadCropsRequest(), new LoadSoilTypesRequest(), new LoadCropAidsRequest()];
      }),
    ),
  );

  loadCrops$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.LoadCropsRequest),
      mergeMap((_) =>
        this.cropService.getCrops().pipe(
          map((crops) => new AppActions.LoadCropsSuccess(crops)),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadCropVarietiesByCropId$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.LoadCropVarietiesByCropIdRequest),
      mergeMap((action: LoadCropVarietiesByCropIdRequest) =>
        this.cropVarietyService.getCropVarietiesByCropId(action.cropId).pipe(
          map((cropVarieties) => new AppActions.LoadCropVarietiesByCropIdSuccess(action.cropId, cropVarieties)),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadSoilTypes$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.LoadSoilTypesRequest),
      mergeMap((_) =>
        this.soilTypeService.getSoilTypes().pipe(
          map((soilTypes) => new AppActions.LoadSoilTypesSuccess(soilTypes)),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadSalesOrderPeriodsRequest$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.AppActionTypes.LoadSalesOrderPeriodsRequest),
        mergeMap((action: LoadSalesOrderPeriodsRequest) =>
          this.salesOrderPeriodService.getSalesOrderPeriods(action.year).pipe(
            tap((salesOrderPeriods) => {
              this.store.dispatch(new AppActions.LoadSalesOrderPeriodsSuccess(salesOrderPeriods));
            }),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          ),
        ),
      ),
    { dispatch: false },
  );

  loadCropAidsRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.LoadCropAidsRequest),
      mergeMap((_) =>
        this.cropAidService.getCropAids().pipe(
          map((cropAids) => new AppActions.LoadCropAidsSuccess(cropAids)),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  downloadAdviceReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.DownloadAdviceReport),
      mergeMap(({ year }) =>
        this.fieldReportService.downloadAdviceReport(year).pipe(
          tap(this.blobService.download),
          map(() => new AppActions.DownloadAdviceReportSuccess()),
          catchError((_: HttpResponse<Blob>) =>
            of(
              NotificationActions.warningSnackbar({
                message: this.locale.translate('downloadFailed'),
              }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  downloadManureSpaceReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.DownloadManureSpaceReport),
      mergeMap(({ year }) =>
        this.fieldReportService.downloadManureSpaceReport(year).pipe(
          tap(this.blobService.download),
          map(() => new AppActions.DownloadManureSpaceReportSuccess()),
          catchError((_: HttpResponse<Blob>) =>
            of(
              NotificationActions.warningSnackbar({
                message: this.locale.translate('downloadFailed'),
              }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  openTotalNitrogenLegalNormImportForm$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.AppActionTypes.OpenTotalNitrogenLegalNormImportForm),
        delay(10),
        switchMap((_) =>
          this.dialog
            .dialog(ImportComponent, {
              title: `${this.locale.translate('importOne')}
              ${this.locale.translate('totalNitrogenLegalNorm').toLowerCase()}
              `,
              cancelButtonText: this.locale.translate('cancel'),
              confirmButtonText: this.locale.translate('save'),
            })
            .pipe(
              switchMap(({ status, result }) =>
                status === DIALOG_RETURN_TYPES.confirm && result.file && result.calendarYear
                  ? this.importService
                      .upsertTotalNitrogenLegalNorm({
                        totalNitrogenLegalNorms: result.file,
                        calendarYear: result.calendarYear,
                      })
                      .pipe(
                        startWith(CoreActions.setLoading({ loading: true })),
                        finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
                      )
                  : EMPTY,
              ),
            ),
        ),
      ),
    { dispatch: false },
  );

  openLegalNitrogenWorkingCoefficientImportForm$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.AppActionTypes.OpenLegalNitrogenWorkingCoefficientImportForm),
        delay(10),
        switchMap((_) =>
          this.dialog
            .dialog(ImportComponent, {
              title: `${this.locale.translate('importOne')}
              ${this.locale.translate('legalNitrogenWorkingCoefficient').toLowerCase()}
              `,
              cancelButtonText: this.locale.translate('cancel'),
              confirmButtonText: this.locale.translate('save'),
            })
            .pipe(
              switchMap(({ status, result }) =>
                status === DIALOG_RETURN_TYPES.confirm && result.file && result.calendarYear
                  ? this.importService
                      .upsertLegalNitrogenWorkingCoefficients({
                        legalNitrogenWorkingCoefficients: result.file,
                        calendarYear: result.calendarYear,
                      })
                      .pipe(
                        startWith(CoreActions.setLoading({ loading: true })),
                        finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
                      )
                  : EMPTY,
              ),
            ),
        ),
      ),
    { dispatch: false },
  );

  openCropVarietySpecificNormsImportForm$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.AppActionTypes.OpenCropVarietySpecificNormsImportForm),
        delay(10),
        switchMap((_) =>
          this.dialog
            .dialog(ImportComponent, {
              title: `${this.locale.translate('importOne')}
              ${this.locale.translate('cropVarietySpecificNorm').toLowerCase()}
              `,
              cancelButtonText: this.locale.translate('cancel'),
              confirmButtonText: this.locale.translate('save'),
            })
            .pipe(
              switchMap(({ status, result }) =>
                status === DIALOG_RETURN_TYPES.confirm && result.file && result.calendarYear
                  ? this.importService
                      .upsertCropVarietySpecificNorm({
                        cropVarietySpecificNorms: result.file,
                        calendarYear: result.calendarYear,
                      })
                      .pipe(
                        startWith(CoreActions.setLoading({ loading: true })),
                        finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
                      )
                  : EMPTY,
              ),
            ),
        ),
      ),
    { dispatch: false },
  );

  openPhosphateLegalNormImportForm$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.AppActionTypes.OpenPhosphateLegalNormImportForm),
        delay(10),
        switchMap((_) =>
          this.dialog
            .dialog(ImportPhosphateLegalNormComponent, {
              title: `${this.locale.translate('importOne')}
              ${this.locale.translate('phosphateLegalNorm').toLowerCase()}
              `,
              cancelButtonText: this.locale.translate('cancel'),
              confirmButtonText: this.locale.translate('save'),
            })
            .pipe(
              switchMap(({ status, result }) =>
                status === DIALOG_RETURN_TYPES.confirm && result.file1 && result.file2 && result.calendarYear
                  ? this.importService
                      .upsertPhosphateLegalNorm({
                        phosphateLegalNorms: result.file1,
                        phosphateTransitionLegalNorms: result.file2,
                        calendarYear: result.calendarYear,
                      })
                      .pipe(
                        startWith(CoreActions.setLoading({ loading: true })),
                        finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
                      )
                  : EMPTY,
              ),
            ),
        ),
      ),
    { dispatch: false },
  );

  loadTotalNitrogenLegalNorms$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.LoadTotalNitrogenLegalNormsRequest),
      mergeMap(({ pageNumber, pageSize, filter }) =>
        this.totalNitrogenLegalNormService.getTotalNitrogenLegalNorms(pageNumber, pageSize, filter).pipe(
          map(({ totalNitrogenLegalNorms, pagination }) => new AppActions.LoadTotalNitrogenLegalNormsSuccess(totalNitrogenLegalNorms, pagination, filter)),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  upsertTotalNitrogenLegalNorm$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.UpsertTotalNitrogenLegalNormRequest),
      map((action: AppActions.UpsertTotalNitrogenLegalNormRequest) => action.totalNitrogenLegalNorm),
      mergeMap((totalNitrogenLegalNormToUpsert: TotalNitrogenLegalNorm) =>
        this.totalNitrogenLegalNormService.upsertTotalNitrogenLegalNorm(totalNitrogenLegalNormToUpsert).pipe(
          map(
            (totalNitrogenLegalNorm) =>
              new AppActions.UpsertTotalNitrogenLegalNormSuccess({
                ...totalNitrogenLegalNormToUpsert,
                ...totalNitrogenLegalNorm,
              }),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  deleteTotalNitrogenLegalNorm$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.DeleteTotalNitrogenLegalNormRequest),
      map((action: AppActions.DeleteTotalNitrogenLegalNormRequest) => action.totalNitrogenLegalNorm),
      mergeMap((totalNitrogenLegalNormToDelete: TotalNitrogenLegalNorm) =>
        this.totalNitrogenLegalNormService.deleteTotalNitrogenLegalNorm(totalNitrogenLegalNormToDelete.id).pipe(
          map(
            (totalNitrogenLegalNorm) =>
              new AppActions.DeleteTotalNitrogenLegalNormSuccess({
                ...totalNitrogenLegalNormToDelete,
                ...totalNitrogenLegalNorm,
              }),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  addTotalNitrogenLegalNorm$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.AddTotalNitrogenLegalNorm),
      map(
        (_) =>
          new AppActions.EditTotalNitrogenLegalNorm({
            id: 0,
            cropId: null,
            calendarYear: null,
            agrifirmCropId: null,
            cropName: '',
            soilTypeId: null,
            nitrogenNorm: 0,
            location: null,
            isPreviousCropCorn: false,
            isPreviousCropNotGrain: false,
            grasslandUsage: null,
            shouldSplitNorm: false,
            cropVarietySpecificNorm: null,
            startDateBefore: null,
            startDateAfter: null,
            endDateBefore: null,
            endDateAfter: null,
            startDatePreviousYearBefore: null,
            startDatePreviousYearAfter: null,
            isNitrogenDifferentiation: false,
            isAfterTornGrass: false,
            isGroundWaterPreservationWithNitrogenCuts: false,
            isNutrientsPollutedArea: false,
          }),
      ),
    ),
  );

  editTotalNitrogenLegalNorm$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.EditTotalNitrogenLegalNorm),
      withLatestFrom(this.store.pipe(select(getCrops)), this.store.pipe(select(getSoilTypes))),
      mergeMap(([action, crops, soilTypes]: [AppActions.EditTotalNitrogenLegalNorm, Crop[], SoilType[]]) =>
        this.dialog
          .form(TotalNitrogenLegalNormDialogComponent, {
            totalNitrogenLegalNorm: {
              ...action.norm,
            },
            crops,
            soilTypes,
            title: action.norm.id ? this.locale.translate('editTotalNitrogenLegalNorm') : this.locale.translate('addTotalNitrogenLegalNorm'),
          })
          .pipe(
            switchMap(({ status, result }) => {
              return status === DIALOG_RETURN_TYPES.confirm ? of(new AppActions.UpsertTotalNitrogenLegalNormRequest(result)) : EMPTY;
            }),
          ),
      ),
    ),
  );

  deleteTotalNitrogenLegalNormRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.DeleteTotalNitrogenLegalNorm),
      mergeMap((action: AppActions.DeleteTotalNitrogenLegalNorm) => of(new AppActions.DeleteTotalNitrogenLegalNormRequest(action.totalNitrogenLegalNorm))),
    ),
  );

  loadLegalNitrogenWorkingCoefficients$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.LoadLegalNitrogenWorkingCoefficientsRequest),
      mergeMap(({ pageNumber, pageSize, filter }) =>
        this.legalNitrogenWorkingCoefficientService.getLegalNitrogenWorkingCoefficients(pageNumber, pageSize, filter).pipe(
          map(
            ({ legalNitrogenWorkingCoefficients, pagination }) =>
              new AppActions.LoadLegalNitrogenWorkingCoefficientsSuccess(legalNitrogenWorkingCoefficients, pagination, filter),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  upsertLegalNitrogenWorkingCoefficient$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.UpsertLegalNitrogenWorkingCoefficientRequest),
      map((action: AppActions.UpsertLegalNitrogenWorkingCoefficientRequest) => action.legalNitrogenWorkingCoefficient),
      mergeMap((legalNitrogenWorkingCoefficientToUpsert: LegalNitrogenWorkingCoefficient) =>
        this.legalNitrogenWorkingCoefficientService.upsertLegalNitrogenWorkingCoefficient(legalNitrogenWorkingCoefficientToUpsert).pipe(
          map(
            (legalNitrogenWorkingCoefficient) =>
              new AppActions.UpsertLegalNitrogenWorkingCoefficientSuccess({
                ...legalNitrogenWorkingCoefficientToUpsert,
                ...legalNitrogenWorkingCoefficient,
              }),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  deleteLegalNitrogenWorkingCoefficient$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.DeleteLegalNitrogenWorkingCoefficientRequest),
      map((action: AppActions.DeleteLegalNitrogenWorkingCoefficientRequest) => action.legalNitrogenWorkingCoefficient),
      mergeMap((legalNitrogenWorkingCoefficientToDelete: LegalNitrogenWorkingCoefficient) =>
        this.legalNitrogenWorkingCoefficientService.deleteLegalNitrogenWorkingCoefficient(legalNitrogenWorkingCoefficientToDelete.id).pipe(
          map(
            (legalNitrogenWorkingCoefficient) =>
              new AppActions.DeleteLegalNitrogenWorkingCoefficientSuccess({
                ...legalNitrogenWorkingCoefficientToDelete,
                ...legalNitrogenWorkingCoefficient,
              }),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  addLegalNitrogenWorkingCoefficient$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.AddLegalNitrogenWorkingCoefficient),
      map(
        (_) =>
          new AppActions.EditLegalNitrogenWorkingCoefficient({
            id: 0,
            calendarYear: null,
            nitrogenWorkingCoefficient: null,
            fertilizerType: null,
            cropAidId: null,
            soilTypeId: null,
            salesOrderPeriodId: null,
          }),
      ),
    ),
  );

  editLegalNitrogenWorkingCoefficient$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.EditLegalNitrogenWorkingCoefficient),
      mergeMap((action: EditLegalNitrogenWorkingCoefficient) =>
        this.dialog
          .form(LegalNitrogenWorkingCoefficientDialogComponent, {
            title: this.locale.translate(
              action?.legalNitrogenWorkingCoefficient?.id ? 'editLegalNitrogenWorkingCoefficient' : 'addLegalNitrogenWorkingCoefficient',
            ),
            legalNitrogenWorkingCoefficient: {
              ...action.legalNitrogenWorkingCoefficient,
            },
          })
          .pipe(
            switchMap(({ status, result }) => {
              return status === DIALOG_RETURN_TYPES.confirm ? of(new AppActions.UpsertLegalNitrogenWorkingCoefficientRequest(result)) : EMPTY;
            }),
          ),
      ),
    ),
  );

  deleteLegalNitrogenWorkingCoefficientRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.DeleteLegalNitrogenWorkingCoefficient),
      mergeMap((action: AppActions.DeleteLegalNitrogenWorkingCoefficient) =>
        of(new AppActions.DeleteLegalNitrogenWorkingCoefficientRequest(action.legalNitrogenWorkingCoefficient)),
      ),
    ),
  );

  loadPhosphateLegalWorkingCoefficients$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.LoadPhosphateLegalWorkingCoefficientsRequest),
      mergeMap(({ pageNumber, pageSize, filter }) =>
        this.phosphateLegalWorkingCoefficientService.getPhosphateLegalWorkingCoefficients(pageNumber, pageSize, filter).pipe(
          map(
            ({ phosphateLegalWorkingCoefficients, pagination }) =>
              new AppActions.LoadPhosphateLegalWorkingCoefficientsSuccess(phosphateLegalWorkingCoefficients, pagination, filter),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  upsertPhosphateLegalWorkingCoefficient$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.UpsertPhosphateLegalWorkingCoefficientRequest),
      map((action: AppActions.UpsertPhosphateLegalWorkingCoefficientRequest) => action.phosphateLegalWorkingCoefficient),
      mergeMap((phosphateLegalWorkingCoefficientToUpsert: PhosphateLegalWorkingCoefficient) =>
        this.phosphateLegalWorkingCoefficientService.upsertPhosphateLegalWorkingCoefficient(phosphateLegalWorkingCoefficientToUpsert).pipe(
          map(
            (phosphateLegalWorkingCoefficient) =>
              new AppActions.UpsertPhosphateLegalWorkingCoefficientSuccess({
                ...phosphateLegalWorkingCoefficientToUpsert,
                ...phosphateLegalWorkingCoefficient,
              }),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  deletePhosphateLegalWorkingCoefficient$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.DeletePhosphateLegalWorkingCoefficientRequest),
      map((action: AppActions.DeletePhosphateLegalWorkingCoefficientRequest) => action.phosphateLegalWorkingCoefficient),
      mergeMap((phosphateLegalWorkingCoefficientToDelete: PhosphateLegalWorkingCoefficient) =>
        this.phosphateLegalWorkingCoefficientService.deletePhosphateLegalWorkingCoefficient(phosphateLegalWorkingCoefficientToDelete.id).pipe(
          map(
            (phosphateLegalWorkingCoefficient) =>
              new AppActions.DeletePhosphateLegalWorkingCoefficientSuccess({
                ...phosphateLegalWorkingCoefficientToDelete,
                ...phosphateLegalWorkingCoefficient,
              }),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  addPhosphateLegalWorkingCoefficient$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.AddPhosphateLegalWorkingCoefficient),
      map(
        (_) =>
          new AppActions.EditPhosphateLegalWorkingCoefficient({
            id: 0,
            calendarYear: null,
            phosphateWorkingCoefficient: null,
            cropAidId: null,
          }),
      ),
    ),
  );

  editPhosphateLegalWorkingCoefficient$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.EditPhosphateLegalWorkingCoefficient),
      mergeMap((action: AppActions.EditPhosphateLegalWorkingCoefficient) =>
        this.dialog
          .form(PhosphateLegalWorkingCoefficientDialogComponent, {
            title: this.locale.translate(
              action?.phosphateLegalWorkingCoefficient?.id ? 'editPhosphateLegalWorkingCoefficient' : 'addPhosphateLegalWorkingCoefficient',
            ),
            phosphateLegalWorkingCoefficient: {
              ...action.phosphateLegalWorkingCoefficient,
            },
          })
          .pipe(
            switchMap(({ status, result }) => {
              return status === DIALOG_RETURN_TYPES.confirm ? of(new AppActions.UpsertPhosphateLegalWorkingCoefficientRequest(result)) : EMPTY;
            }),
          ),
      ),
    ),
  );

  deletePhosphateLegalWorkingCoefficientRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.DeletePhosphateLegalWorkingCoefficient),
      mergeMap((action: AppActions.DeletePhosphateLegalWorkingCoefficient) =>
        of(new AppActions.DeletePhosphateLegalWorkingCoefficientRequest(action.phosphateLegalWorkingCoefficient)),
      ),
    ),
  );

  loadCropVarietySpecificNorms$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.LoadCropVarietySpecificNormsRequest),
      mergeMap(({ pageNumber, pageSize, filter }) =>
        this.cropVarietySpecificNormService.getCropVarietySpecificNorms(pageNumber, pageSize, filter).pipe(
          map(({ cropVarietySpecificNorms, pagination }) => new AppActions.LoadCropVarietySpecificNormsSuccess(cropVarietySpecificNorms, pagination, filter)),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  upsertCropVarietySpecificNorm$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.UpsertCropVarietySpecificNormRequest),
      map((action: AppActions.UpsertCropVarietySpecificNormRequest) => action.cropVarietySpecificNorm),
      mergeMap((cropVarietySpecificNormToUpsert: CropVarietySpecificNorm) =>
        this.cropVarietySpecificNormService.upsertCropVarietySpecificNorm(cropVarietySpecificNormToUpsert).pipe(
          map(
            (cropVarietySpecificNorm) =>
              new AppActions.UpsertCropVarietySpecificNormSuccess({
                ...cropVarietySpecificNormToUpsert,
                ...cropVarietySpecificNorm,
              }),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  deleteCropVarietySpecificNorm$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.DeleteCropVarietySpecificNormRequest),
      map((action: AppActions.DeleteCropVarietySpecificNormRequest) => action.cropVarietySpecificNorm),
      mergeMap((cropVarietySpecificNormToDelete: CropVarietySpecificNorm) =>
        this.cropVarietySpecificNormService.deleteCropVarietySpecificNorm(cropVarietySpecificNormToDelete.id).pipe(
          map(
            (cropVarietySpecificNorm) =>
              new AppActions.DeleteCropVarietySpecificNormSuccess({
                ...cropVarietySpecificNormToDelete,
                ...cropVarietySpecificNorm,
              }),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  addCropVarietySpecificNorm$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.AddCropVarietySpecificNorm),
      map(
        (_) =>
          new AppActions.EditCropVarietySpecificNorm({
            id: 0,
            calendarYear: null,
            cropId: null,
            agrifirmCropId: null,
            cropName: null,
            cropVarietyId: null,
            ediCode: null,
            cropVarietyName: null,
            cropVarietySpecificNorm: null,
            isNitrogenDifferentiation: false,
          }),
      ),
    ),
  );

  editCropVarietySpecificNorm$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.EditCropVarietySpecificNorm),
      withLatestFrom(this.store.pipe(select(getCrops))),
      mergeMap(([action, crops]: [AppActions.EditCropVarietySpecificNorm, Crop[]]) =>
        this.dialog
          .form(CropVarietySpecificNormDialogComponent, {
            cropVarietySpecificNorm: {
              ...action.norm,
            },
            crops,
            title: action.norm.id ? this.locale.translate('editCropVarietySpecificNorm') : this.locale.translate('addCropVarietySpecificNorm'),
          })
          .pipe(
            switchMap(({ status, result }) => {
              return status === DIALOG_RETURN_TYPES.confirm ? of(new AppActions.UpsertCropVarietySpecificNormRequest(result)) : EMPTY;
            }),
          ),
      ),
    ),
  );

  deleteCropVarietySpecificNormRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.DeleteCropVarietySpecificNorm),
      mergeMap((action: AppActions.DeleteCropVarietySpecificNorm) => of(new AppActions.DeleteCropVarietySpecificNormRequest(action.cropVarietySpecificNorm))),
    ),
  );

  loadPhosphateLegalNorms$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.LoadPhosphateLegalNormsRequest),
      mergeMap(({ pageNumber, pageSize, filter }) =>
        this.phosphateLegalNormService.getPhosphateLegalNorms(pageNumber, pageSize, filter).pipe(
          map(({ phosphateLegalNorms, pagination }) => new AppActions.LoadPhosphateLegalNormsSuccess(phosphateLegalNorms, pagination, filter)),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  upsertPhosphateLegalNorm$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.UpsertPhosphateLegalNormRequest),
      map((action: AppActions.UpsertPhosphateLegalNormRequest) => action.phosphateLegalNorm),
      mergeMap((phosphateLegalNormToUpsert: PhosphateLegalNorm) =>
        this.phosphateLegalNormService.upsertPhosphateLegalNorm(phosphateLegalNormToUpsert).pipe(
          map(
            (phosphateLegalNorm) =>
              new AppActions.UpsertPhosphateLegalNormSuccess({
                ...phosphateLegalNormToUpsert,
                ...phosphateLegalNorm,
              }),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  deletePhosphateLegalNorm$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.DeletePhosphateLegalNormRequest),
      map((action: AppActions.DeletePhosphateLegalNormRequest) => action.phosphateLegalNorm),
      mergeMap((phosphateLegalNormToDelete: PhosphateLegalNorm) =>
        this.phosphateLegalNormService.deletePhosphateLegalNorm(phosphateLegalNormToDelete.id).pipe(
          map(
            (phosphateLegalNorm) =>
              new AppActions.DeletePhosphateLegalNormSuccess({
                ...phosphateLegalNormToDelete,
                ...phosphateLegalNorm,
              }),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  addPhosphateLegalNorm$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.AddPhosphateLegalNorm),
      map(
        (_) =>
          new AppActions.EditPhosphateLegalNorm({
            id: 0,
            cropId: null,
            agrifirmCropId: null,
            calendarYear: null,
            pCaClFromAndIncluding: null,
            pCaClToAndIncluding: null,
            pAlFromAndIncluding: null,
            pAlToAndIncluding: null,
            pwFromAndIncluding: null,
            pwToAndIncluding: null,
            condition: PhosphateConditionEnum.Unknown,
            phosphateNorm: null,
            poorInPhosphateNorm: null,
            isTransitionVariant: false,
          }),
      ),
    ),
  );

  editPhosphateLegalNorm$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.EditPhosphateLegalNorm),
      withLatestFrom(this.store.pipe(select(getCrops)), this.store.pipe(select(getSoilTypes))),
      mergeMap(([action, crops, soilTypes]: [AppActions.EditPhosphateLegalNorm, Crop[], SoilType[]]) =>
        this.dialog
          .form(PhosphateLegalNormDialogComponent, {
            phosphateLegalNorm: {
              ...action.norm,
            },
            crops,
            soilTypes,
            title: action.norm.id ? this.locale.translate('editPhosphateLegalNorm') : this.locale.translate('addPhosphateLegalNorm'),
          })
          .pipe(
            switchMap(({ status, result }) => {
              return status === DIALOG_RETURN_TYPES.confirm ? of(new AppActions.UpsertPhosphateLegalNormRequest(result)) : EMPTY;
            }),
          ),
      ),
    ),
  );

  deletePhosphateLegalNormRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.AppActionTypes.DeletePhosphateLegalNorm),
      mergeMap((action: AppActions.DeletePhosphateLegalNorm) => of(new AppActions.DeletePhosphateLegalNormRequest(action.phosphateLegalNorm))),
    ),
  );
}
